import { FilterField, PageHeader } from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { Edit01 } from "@untitledui/icons";
import { useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type Action, type Column, TableCustom } from "@/components/ui/table";
import {
	motoristaBuscar,
	motoristaEditar,
	motoristaNovoLote,
} from "@/services/analytics/events";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import {
	CategoryFilter,
	CnhStatusCard,
	CopyField,
	DriversListActions,
} from "../components";
import BatchRegister from "../components/BatchRegister";
import EditDriverModal from "../components/EditDriverModal";
import { CNH_STATUS_PAGE_SIZE, DRIVERS_PAGE_SIZE } from "../constants";
import {
	useDrivers,
	useDriversExpired30Days,
	useDriversExpiredCnh,
	useDriversWithoutCnh,
} from "../hooks/useDriversQueries";
import type { CnhStatusCardDriver, Driver, PaginatedDrivers } from "../types";
import { formatCpfMasked } from "../utils";
import { normalizeCategoryCnh } from "../utils/list-drivers.helpers";

const SEARCH_DEBOUNCE_MS = 500;
const trimSearch = (value: string) => value.trim();

const cnhStatusCardData = (data: PaginatedDrivers | undefined) => ({
	count: data?.totalCount ?? 0,
	currentPage: data?.currentPage ?? 1,
	items: data?.items ?? [],
});

const DriversPage = () => {
	const navigate = useNavigate();
	const cnhSectionTitleId = useId();
	const categoryInputId = useId();

	const [currentPage, setCurrentPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategories, setSelectedCategories] = useState<
		readonly { value: string; label: string }[]
	>([]);
	const [isBatchOpen, setIsBatchOpen] = useState(false);
	const [editingDriver, setEditingDriver] =
		useState<CnhStatusCardDriver | null>(null);
	const [withoutCnhPage, setWithoutCnhPage] = useState(1);
	const [expired30DaysPage, setExpired30DaysPage] = useState(1);
	const [expiredCnhPage, setExpiredCnhPage] = useState(1);
	const [totalDriversCount, setTotalDriversCount] = useState(0);

	useDebouncedCommit({
		value: searchInput,
		applied: searchTerm,
		delay: SEARCH_DEBOUNCE_MS,
		normalize: trimSearch,
		onCommit: (term) => {
			setSearchTerm(term);
			setCurrentPage(1);
		},
	});

	useEffect(() => {
		if (searchTerm) {
			motoristaBuscar(searchTerm);
		}
	}, [searchTerm]);

	const isFiltering =
		searchInput.trim() !== "" || selectedCategories.length > 0;

	const category = normalizeCategoryCnh(
		selectedCategories.map((c) => c.value).join(",")
	).join(",");

	const driversQuery = useDrivers({
		PageNumber: currentPage,
		PageSize: DRIVERS_PAGE_SIZE,
		Search: searchTerm,
		Category: category,
	});
	const { data, isError, isLoading, isFetching, refetch } = driversQuery;

	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria: { search: searchTerm, category, pageSize: DRIVERS_PAGE_SIZE },
		totalPages: data?.totalPages,
		query: driversQuery,
	});

	useEffect(() => {
		if (!(isLoading || isFiltering)) {
			setTotalDriversCount(data?.totalCount ?? 0);
		}
	}, [isLoading, isFiltering, data?.totalCount]);

	const hasDrivers = totalDriversCount > 0;

	const hasFilteredResults = isFiltering
		? (data?.items?.length ?? 0) > 0
		: hasDrivers;

	const { data: driversWithoutCnh } = useDriversWithoutCnh({
		PageNumber: withoutCnhPage,
		PageSize: CNH_STATUS_PAGE_SIZE,
	});
	const { data: driversExpiringSoon } = useDriversExpired30Days({
		PageNumber: expired30DaysPage,
		PageSize: CNH_STATUS_PAGE_SIZE,
	});
	const { data: driversWithExpiredCnh } = useDriversExpiredCnh({
		PageNumber: expiredCnhPage,
		PageSize: CNH_STATUS_PAGE_SIZE,
	});

	const withoutCnhTotalPages = driversWithoutCnh?.totalPages ?? 1;
	const expired3DaysTotalPages = driversExpiringSoon?.totalPages ?? 1;
	const expiredCnhTotalPages = driversWithExpiredCnh?.totalPages ?? 1;

	const handleNextWithoutCnhPage = () => {
		setWithoutCnhPage((p) => (p < withoutCnhTotalPages ? p + 1 : p));
	};

	const handleNextExpired30DaysPage = () => {
		setExpired30DaysPage((p) => (p < expired3DaysTotalPages ? p + 1 : p));
	};

	const handleNextExpiredCnhPage = () => {
		setExpiredCnhPage((p) => (p < expiredCnhTotalPages ? p + 1 : p));
	};

	const columns: Column<Driver>[] = [
		{ id: "name", label: "Motoristas" },
		{ id: "cpf", label: "CPF", render: (row) => formatCpfMasked(row.cpf) },
		{ id: "function", label: "Função" },
		{
			id: "vehiclePlate",
			label: "Placa",
			render: (row) => row.vehicle?.plate ?? "-",
		},
		{
			id: "categoryCnh",
			label: "Categoria",
			render: (row) => {
				const categories = normalizeCategoryCnh(row.categoryCnh);
				return categories.length ? categories.join(", ") : "-";
			},
		},
	];

	const actions: Action<Driver>[] = useMemo(
		() => [
			{
				icon: Edit01,
				label: "Editar",
				onClick: (record: Driver) => {
					motoristaEditar(record.id);
					setEditingDriver(record);
				},
			},
		],
		[]
	);

	const tenantCode =
		typeof window !== "undefined" ? window.location.hostname.split(".")[0] : "";

	return (
		<>
			<BatchRegister
				isOpen={isBatchOpen}
				onClose={() => setIsBatchOpen(false)}
			/>

			<EditDriverModal
				driverId={editingDriver?.id ?? null}
				isOpen={!!editingDriver}
				onClose={() => setEditingDriver(null)}
			/>

			<div className="w-full space-y-6 px-6 py-8">
				<PageHeader
					actions={<CopyField label="Código da empresa" value={tenantCode} />}
					className="items-start"
					title="Motoristas"
				/>

				<section
					aria-labelledby={cnhSectionTitleId}
					className="mb-12 flex flex-col gap-7 rounded-xl border border-secondary p-6 shadow-xs"
				>
					<h2
						className="font-semibold text-lg text-primary leading-7"
						id={cnhSectionTitleId}
					>
						Status da CNH
					</h2>
					<div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
						<CnhStatusCard
							{...cnhStatusCardData(driversWithoutCnh)}
							descriptionIfEmpty="Todos os motoristas possuem CNH cadastrada."
							descriptionIfFilled={(count) =>
								`Você possui ${count} motorista(s) sem CNH cadastrada.`
							}
							onNextPage={handleNextWithoutCnhPage}
							onPrevPage={() => setWithoutCnhPage((p) => Math.max(p - 1, 1))}
							onSelectDriver={(driver) => setEditingDriver(driver)}
							title="Motoristas sem CNH cadastrada"
							totalPages={withoutCnhTotalPages}
						/>

						<CnhStatusCard
							{...cnhStatusCardData(driversExpiringSoon)}
							descriptionIfEmpty="Você não possui CNHs próxima(s) do vencimento."
							descriptionIfFilled={(count) =>
								`Você possui ${count} CNH(s) próxima(s) do vencimento.`
							}
							onNextPage={handleNextExpired30DaysPage}
							onPrevPage={() => setExpired30DaysPage((p) => Math.max(p - 1, 1))}
							onSelectDriver={(driver) => setEditingDriver(driver)}
							title="A vencer nos próximos 30 dias"
							totalPages={expired3DaysTotalPages}
						/>

						<CnhStatusCard
							{...cnhStatusCardData(driversWithExpiredCnh)}
							descriptionIfEmpty="Você não possui CNHs vencida(s)."
							descriptionIfFilled={(count) =>
								`Você possui ${count} CNH(s) vencida(s).`
							}
							onNextPage={handleNextExpiredCnhPage}
							onPrevPage={() => setExpiredCnhPage((p) => Math.max(p - 1, 1))}
							onSelectDriver={(driver) => setEditingDriver(driver)}
							title="Motoristas com CNH vencida"
							totalPages={expiredCnhTotalPages}
						/>
					</div>
				</section>

				<TableCustom
					actions={actions}
					className="p-0"
					columns={columns}
					currentPage={currentPage}
					data={data?.items ?? []}
					disableSearch={
						!hasDrivers ||
						(selectedCategories.length > 0 && !hasFilteredResults)
					}
					errorMessage="Erro ao carregar motoristas."
					extraActions={
						<DriversListActions
							hasResults={hasFilteredResults}
							onClickBatchRegister={() => {
								motoristaNovoLote();
								setIsBatchOpen(true);
							}}
						/>
					}
					filterBarLabel="Filtros de motoristas"
					filters={
						<FilterField
							htmlFor={categoryInputId}
							label="Categorias da CNH"
							labelVisibility="hidden"
						>
							<CategoryFilter
								hasAnyDrivers={hasDrivers}
								inputId={categoryInputId}
								onPageChange={setCurrentPage}
								onSelectedCategoriesChange={setSelectedCategories}
								selectedCategories={selectedCategories}
							/>
						</FilterField>
					}
					isError={isError}
					isLoading={isLoading || isFetching}
					layout="page"
					noDataMessage={
						<div className="flex flex-col items-center justify-center gap-2.5">
							<span>Não há motoristas cadastrados</span>
							<span className="text-tertiary">
								Cadastre motoristas para obter informações
							</span>
							<Button
								color="primary"
								onClick={() => navigate("/users")}
								size="sm"
							>
								Cadastrar motorista
							</Button>
						</div>
					}
					onPageChange={setCurrentPage}
					onRetry={() => {
						refetch();
					}}
					onSearchChange={(value) => {
						setSearchInput(value);
						setCurrentPage(1);
					}}
					searchPlaceholder="Buscar por nome, CPF ou placa"
					searchValue={searchInput}
					title="Motoristas cadastrados"
					titleAs="h2"
					titleClassName="text-lg leading-7"
					totalPages={totalPages}
				/>
			</div>
		</>
	);
};

export default DriversPage;
